import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  FormControlLabel,
  InputAdornment,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import WarningIcon from "@material-ui/icons/Warning";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { CustomDialog, Toaster } from "@/components";
import UserContext from "@/contexts/UserContext";
import {
  createDraftService,
  getCommerceDgiiInfoService,
  getDraftErrorMessage,
  getValidateIdentificationErrorMessage,
  validateIdentificationService,
} from "@/services/assistedAffiliation";
import {
  accountTypeOptions,
  COMMERCIAL_NAME_REQUIRED_MESSAGE,
  CREATE_COMMERCE_CONFIRMATION_MODAL,
  connectivityOptions,
  DRAFT_CREATE_ERROR_MESSAGE,
  DRAFT_CREATE_NAMES_REQUIRED_MESSAGE,
  DRAFT_CREATE_VALIDATION_BLOCKED_MESSAGE,
  DRAFT_CREATE_VALIDATION_REQUIRED_MESSAGE,
  DRAFT_FIELDS_INVALID_MESSAGE,
  DRAFT_SAVE_ERROR_MESSAGE,
  DRAFT_SAVE_SUCCESS_MESSAGE,
  INTERNAL_COMMENTS_MAX_LENGTH,
  OBSERVATION_FALLBACK_AUTHOR,
  buildCreationStatusOptions,
  initialAssistedAffiliationForm,
  initialAssistedAffiliationFiles,
  initialLegalDocumentFiles,
  legalDocuments,
  monthlySalesOptions,
  nationalityOptions,
  yesNoOptions,
} from "../constants";
import {
  AlertState,
  AssistedAffiliationFiles,
  AssistedAffiliationFieldName,
  AssistedAffiliationForm,
  LegalDocumentFileName,
  LegalDocumentFiles,
  RncValidationFeedback,
  RequestSummaryMetadata,
  SelectOption,
  SingleAssistedAffiliationFileName,
} from "../types";
import AffiliationToggleList from "./AffiliationToggleList";
import FileUploadField from "./FileUploadField";
import FormSection from "./FormSection";
import LegalDocumentsUpload from "./LegalDocumentsUpload";
import RequestActionToolbar from "./RequestActionToolbar";
import RequestHistory from "./RequestHistory";
import RequestSummary from "./RequestSummary";
import RncValidationFeedbackCard from "./RncValidationFeedbackCard";
import StatusChangeConfirmationModal from "./StatusChangeConfirmationModal";
import useAssistedAffiliationStyles from "../styles";
import {
  formToDraftPayload,
  parseMembershipRate,
  parseTerminalQuantity,
  type DraftPayloadLabels,
} from "../draftMappers";
import { useAssistedAffiliationCatalogs } from "../hooks/useAssistedAffiliationCatalogs";
import { toStatusObservation } from "../statusCommentFormatter";
import type { AssistedAffiliationDraftObservation } from "@/services/assistedAffiliation/types";
import {
  CommerceCreationGuard,
  createCommerceOnce,
} from "./commerceCreationGuard";
import { addCommercialNameToBilletFeedback } from "../rncValidationFeedback";
import { validateEmail } from "@/utils/emailValidation";
import { useRepresentativeEmailValidation } from "../hooks/useRepresentativeEmailValidation";
import { EMAIL_FORMAT_ERROR, EMAIL_SAVE_ERROR } from "../emailValidationConstants";
import RepresentativeEmailField from "./RepresentativeEmailField";
import MissingRequiredDataModal from "./MissingRequiredDataModal";
import { getMissingRequiredData } from "../requiredDataValidation";
import { buildRequestSummaryRows } from "../requestSummary";
import { withValidatedDraftEmail } from "../draftEmailPayload";

/**
 * Lo que solo existe al editar una solicitud ya creada. Va agrupado para que el tipo no admita
 * combinaciones imposibles, como un formulario poblado que no puede guardar.
 */
type AssistedAffiliationEditMode = {
  /** Siembra el formulario al montar; a partir de ahí manda lo que el usuario escribe. */
  defaultForm: AssistedAffiliationForm;
  /** Fecha ISO que asigna el servicio. */
  lastUpdatedAt: string;
  summary?: RequestSummaryMetadata;
  isSaving: boolean;
  onSave: (form: AssistedAffiliationForm, context: DraftSaveContext) => Promise<void>;
  /**
   * Historial que trajo la consulta. Se muestra al final del formulario y se renueva con la consulta
   * que sigue a cada guardado exitoso.
   */
  observations?: AssistedAffiliationDraftObservation[];
};

/** Lo que el guardado de una solicitud existente lleva además del formulario. */
export type DraftSaveContext = {
  clearRepresentativeEmail?: boolean;
  /** Motivos de los cambios de estado hechos desde el último guardado, en orden. */
  observations: AssistedAffiliationDraftObservation[];
  /** Nombres de los catálogos elegidos, resueltos con las mismas opciones que pinta cada select. */
  labels: DraftPayloadLabels;
};

type AssistedAffiliationManualRequestProps = {
  onBack: () => void;
  /** Cierra el formulario y permite que la pantalla padre refresque la bandeja tras crear. */
  onCreated?: (notice?: AlertState) => void;
  /** Ausente = alta de una solicitud nueva. */
  edit?: AssistedAffiliationEditMode;
};

type FieldErrors = Partial<Record<AssistedAffiliationFieldName, string>>;
type FileErrors = Partial<Record<SingleAssistedAffiliationFileName, string>>;
type TouchedFields = Partial<Record<AssistedAffiliationFieldName, boolean>>;
type TouchedFiles = Partial<Record<SingleAssistedAffiliationFileName, boolean>>;
type LegalDocumentFileErrors = Partial<Record<LegalDocumentFileName, string>>;

const REPRESENTATIVE_FILE_MAX_SIZE = 25 * 1024 * 1024;
const REPRESENTATIVE_DOCUMENT_TYPES = ["image/jpeg", "application/pdf"];
const LEGAL_DOCUMENT_MAX_SIZE = 25 * 1024 * 1024;
const LEGAL_DOCUMENT_TYPES = ["image/jpeg", "application/pdf"];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NUMERIC_PATTERN = /^\d+$/;
// El celular llega con prefijo internacional desde el servicio, así que acepta un `+` inicial.
// Rechazarlo marcaría en rojo un dato que el propio servicio guarda, y quitarlo al mostrarlo haría
// que el guardado reescribiera el número sin él.
const PHONE_PATTERN = /^\+?\d+$/;
const PASSPORT_PATTERN = /^[a-zA-Z0-9]+$/;
const ALPHANUMERIC_TEXT_PATTERN = /^[a-zA-ZÀ-ÿ0-9\s.,&'/-]+$/;

const getRepresentativeFileError = (file: File | null, required: boolean) => {
  if (required && !file) {
    return "Este archivo es requerido.";
  }

  if (!file) {
    return "";
  }

  if (!REPRESENTATIVE_DOCUMENT_TYPES.includes(file.type)) {
    return "Formato permitido: JPG o PDF.";
  }

  if (file.size > REPRESENTATIVE_FILE_MAX_SIZE) {
    return "El archivo no debe superar 25MB.";
  }

  return "";
};

const getLegalDocumentFileError = (file: File) => {
  if (!LEGAL_DOCUMENT_TYPES.includes(file.type)) {
    return "Formato permitido: JPG o PDF.";
  }

  if (file.size > LEGAL_DOCUMENT_MAX_SIZE) {
    return "El archivo no debe superar 25MB.";
  }

  return "";
};

const AssistedAffiliationManualRequest = ({
  onBack,
  onCreated,
  edit,
}: AssistedAffiliationManualRequestProps) => {
  const classes = useAssistedAffiliationStyles();
  const { user } = useContext(UserContext);
  const isEditing = Boolean(edit);
  const [form, setForm] = useState<AssistedAffiliationForm>(
    edit?.defaultForm ?? initialAssistedAffiliationForm
  );
  const emailValidation = useRepresentativeEmailValidation(form.representativeEmail);
  const [isEmailValidationDialogOpen, setIsEmailValidationDialogOpen] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const draftSaveInFlightRef = useRef(false);
  // Sube con cada validación de la identificación: solo la última apaga el indicador de "validando".
  const identificationValidationRef = useRef(0);
  const [files, setFiles] = useState<AssistedAffiliationFiles>(
    initialAssistedAffiliationFiles
  );
  const [legalDocumentFiles, setLegalDocumentFiles] = useState<LegalDocumentFiles>(
    initialLegalDocumentFiles
  );
  const [legalDocumentFileErrors, setLegalDocumentFileErrors] =
    useState<LegalDocumentFileErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [touchedFiles, setTouchedFiles] = useState<TouchedFiles>({});
  const [hasSubmittedForm, setHasSubmittedForm] = useState(false);
  const [rncValidationExecuted, setRncValidationExecuted] = useState(false);
  const [rncValidationFeedback, setRncValidationFeedback] =
    useState<RncValidationFeedback | null>(null);
  const [isLegalNameManuallyEditable, setIsLegalNameManuallyEditable] =
    useState(Boolean(edit && !edit.defaultForm.legalName.trim()));
  const [isValidatingIdentification, setIsValidatingIdentification] =
    useState(false);
  const [pendingCreationStatus, setPendingCreationStatus] = useState("");
  // Motivos confirmados que todavía no se guardaron. Van al historial de la solicitud, no a los
  // comentarios internos.
  const [statusObservations, setStatusObservations] = useState<
    AssistedAffiliationDraftObservation[]
  >([]);
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    type: "info",
    text: "",
  });
  const [isCreatingCommerce, setIsCreatingCommerce] = useState(false);
  const [hasCreatedCommerce, setHasCreatedCommerce] = useState(false);
  const [isCreateConfirmationOpen, setIsCreateConfirmationOpen] = useState(false);
  const [isMissingRequiredDataOpen, setIsMissingRequiredDataOpen] = useState(false);
  const commerceCreationGuardRef = useRef<CommerceCreationGuard>({
    inFlight: false,
    completed: false,
  });
  const {
    catalogs,
    catalogError,
    clearCatalogError,
    loading: catalogLoading,
    loadMunicipalities,
    loadNeighborhoods,
    loadSectors,
  } = useAssistedAffiliationCatalogs();

  // Los IDs guardados necesitan sus catálogos también al abrir una edición.
  // Los cambios manuales limpian los IDs hijos en updateField; cargar opciones no los modifica.
  useEffect(() => {
    loadMunicipalities(form.province);
  }, [form.province, loadMunicipalities]);

  useEffect(() => {
    loadSectors(form.municipality);
  }, [form.municipality, loadSectors]);

  useEffect(() => {
    loadNeighborhoods(form.sector);
  }, [form.sector, loadNeighborhoods]);

  useEffect(() => {
    if (!catalogError) return;

    setAlert({
      show: true,
      type: "error",
      text: catalogError,
    });
    clearCatalogError();
  }, [catalogError, clearCatalogError]);

  const {
    missingRequiredFields,
    missingRequiredLegalDocuments,
    hasMissingRequiredData,
  } = useMemo(
    () => getMissingRequiredData(form, files, legalDocumentFiles),
    [form, files, legalDocumentFiles]
  );
  const visibleLegalDocumentFileErrors = useMemo<LegalDocumentFileErrors>(() => {
    if (!hasSubmittedForm) return legalDocumentFileErrors;

    return legalDocuments.reduce<LegalDocumentFileErrors>((errors, document) => {
      if (
        document.required &&
        legalDocumentFiles[document.field].length === 0 &&
        !errors[document.field]
      ) {
        errors[document.field] = "Este archivo es requerido.";
      }

      return errors;
    }, { ...legalDocumentFileErrors });
  }, [hasSubmittedForm, legalDocumentFileErrors, legalDocumentFiles]);
  const isRncValidationSuccessful = rncValidationFeedback?.tone === "success";
  const hasBlockingRncValidation = Boolean(
    rncValidationFeedback && !isRncValidationSuccessful
  );

  const representativeFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const names = form.representativeNames.trim();
    const lastNames = form.representativeLastNames.trim();
    const email = form.representativeEmail.trim();
    const phone = form.representativePhone.trim();
    // El rango de 10 a 12 cuenta dígitos, así que el prefijo no entra en la cuenta.
    const phoneDigits = phone.replace(/^\+/, "");
    const identity = form.identityNumber.trim();
    const passport = form.passportNumber.trim();

    if (!names) errors.representativeNames = "Este campo es requerido.";
    if (!lastNames) errors.representativeLastNames = "Este campo es requerido.";

    if (!email) {
      errors.representativeEmail = "Este campo es requerido.";
    } else if (!validateEmail(email)) {
      errors.representativeEmail = EMAIL_FORMAT_ERROR;
    } else if (emailValidation.state.status === "invalid" || emailValidation.state.status === "error") {
      errors.representativeEmail = emailValidation.state.message;
    }

    if (!phone) {
      errors.representativePhone = "Este campo es requerido.";
    } else if (!PHONE_PATTERN.test(phone)) {
      errors.representativePhone = "Ingresa solo números.";
    } else if (phoneDigits.length < 10 || phoneDigits.length > 12) {
      errors.representativePhone = "El celular debe tener entre 10 y 12 dígitos.";
    }

    if (form.isForeignRepresentative) {
      if (!form.nationality.trim()) {
        errors.nationality = "Este campo es requerido.";
      }

      if (!passport) {
        errors.passportNumber = "Este campo es requerido.";
      } else if (!PASSPORT_PATTERN.test(passport)) {
        errors.passportNumber = "Ingresa solo letras y números.";
      } else if (passport.length > 9) {
        errors.passportNumber = "El pasaporte no puede superar 9 caracteres.";
      }
    } else if (!identity) {
      errors.identityNumber = "Este campo es requerido.";
    } else if (!NUMERIC_PATTERN.test(identity)) {
      errors.identityNumber = "Ingresa solo números.";
    } else if (identity.length > 11) {
      errors.identityNumber = "La cédula no puede superar 11 dígitos.";
    }

    return errors;
  }, [form, emailValidation.state.status, emailValidation.state.message]);

  const representativeFileErrors = useMemo<FileErrors>(() => {
    const errors: FileErrors = {};

    if (form.isForeignRepresentative) {
      const passportPhotoError = getRepresentativeFileError(files.passportPhoto, true);
      if (passportPhotoError) errors.passportPhoto = passportPhotoError;
      return errors;
    }

    const frontError = getRepresentativeFileError(files.frontIdentification, true);
    const backError = getRepresentativeFileError(files.backIdentification, true);

    if (frontError) errors.frontIdentification = frontError;
    if (backError) errors.backIdentification = backError;

    return errors;
  }, [files, form.isForeignRepresentative]);

  const hasRepresentativeErrors =
    Object.keys(representativeFieldErrors).length > 0 ||
    Object.keys(representativeFileErrors).length > 0;

  const commerceFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const documentNumber = form.documentNumber.trim();
    const legalName = form.legalName.trim();
    const commercialName = form.commercialName.trim();

    if (!documentNumber) {
      errors.documentNumber = "Este campo es requerido.";
    } else if (!NUMERIC_PATTERN.test(documentNumber)) {
      errors.documentNumber = "Ingresa solo números.";
    } else if (documentNumber.length < 9 || documentNumber.length > 11) {
      errors.documentNumber = "El RNC/Cédula debe tener entre 9 y 11 dígitos.";
    }

    if (!commercialName) {
      errors.commercialName = "Este campo es requerido.";
    } else if (!ALPHANUMERIC_TEXT_PATTERN.test(commercialName)) {
      errors.commercialName = "Ingresa solo caracteres alfanuméricos.";
    }

    if (!legalName) errors.legalName = "Este campo es requerido.";
    if (!form.monthlySales.trim()) {
      errors.monthlySales = "Este campo es requerido.";
    }
    if (!String(form.businessType).trim()) {
      errors.businessType = "Este campo es requerido.";
    }

    return errors;
  }, [
    form.businessType,
    form.commercialName,
    form.documentNumber,
    form.legalName,
    form.monthlySales,
  ]);

  const hasCommerceErrors = Object.keys(commerceFieldErrors).length > 0;

  const addressFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const streetAndNumber = form.streetAndNumber.trim();
    const reference = form.reference.trim();

    if (!form.province.trim()) errors.province = "Este campo es requerido.";
    if (!form.municipality.trim()) errors.municipality = "Este campo es requerido.";
    if (!form.sector.trim()) errors.sector = "Este campo es requerido.";
    if (!form.neighborhood.trim()) errors.neighborhood = "Este campo es requerido.";

    if (!streetAndNumber) {
      errors.streetAndNumber = "Este campo es requerido.";
    } else if (!ALPHANUMERIC_TEXT_PATTERN.test(streetAndNumber)) {
      errors.streetAndNumber = "Ingresa solo caracteres alfanuméricos.";
    } else if (streetAndNumber.length > 100) {
      errors.streetAndNumber = "La calle y número no puede superar 100 caracteres.";
    }

    if (reference.length > 100) {
      errors.reference = "La referencia no puede superar 100 caracteres.";
    }

    return errors;
  }, [
    form.municipality,
    form.neighborhood,
    form.province,
    form.reference,
    form.sector,
    form.streetAndNumber,
  ]);

  const accountFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const accountNumber = form.accountNumber.trim();

    if (!form.bank.trim()) errors.bank = "Este campo es requerido.";
    if (!form.accountType.trim()) errors.accountType = "Este campo es requerido.";

    if (!accountNumber) {
      errors.accountNumber = "Este campo es requerido.";
    } else if (!NUMERIC_PATTERN.test(accountNumber)) {
      errors.accountNumber = "Ingresa solo números.";
    }

    return errors;
  }, [form.accountNumber, form.accountType, form.bank]);

  const accountFileErrors = useMemo<FileErrors>(() => {
    const errors: FileErrors = {};
    const accountEvidenceError = getRepresentativeFileError(files.accountEvidence, true);

    if (accountEvidenceError) {
      errors.accountEvidence = accountEvidenceError;
    }

    return errors;
  }, [files.accountEvidence]);

  const executiveFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const executiveEmail = form.executiveEmail.trim();
    const cashManagementEmail = form.cashManagementEmail.trim();

    if (executiveEmail && !EMAIL_PATTERN.test(executiveEmail)) {
      errors.executiveEmail = "Ingresa un correo electrónico válido.";
    }

    if (cashManagementEmail && !EMAIL_PATTERN.test(cashManagementEmail)) {
      errors.cashManagementEmail = "Ingresa un correo electrónico válido.";
    }

    return errors;
  }, [form.cashManagementEmail, form.executiveEmail]);

  const hasExecutiveErrors = Object.keys(executiveFieldErrors).length > 0;

  const billetSmartFieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};
    const rate = form.tapRate.trim();
    const quantity = form.tapQuantity.trim();

    if (rate && parseMembershipRate(rate) === null) {
      errors.tapRate = "Ingresa una tasa entre 0.01 y 4.75, con hasta dos decimales.";
    }

    if (quantity && parseTerminalQuantity(quantity) === null) {
      errors.tapQuantity = "La cantidad debe ser 1, 2 o 3.";
    }

    return errors;
  }, [form.tapQuantity, form.tapRate]);

  const hasBilletSmartErrors = Object.keys(billetSmartFieldErrors).length > 0;
  const hasFormValidationErrors =
    hasRepresentativeErrors ||
    hasCommerceErrors ||
    Object.keys(addressFieldErrors).length > 0 ||
    Object.keys(accountFieldErrors).length > 0 ||
    Object.keys(accountFileErrors).length > 0 ||
    Object.values(legalDocumentFileErrors).some(Boolean) ||
    hasExecutiveErrors ||
    hasBilletSmartErrors;

  const touchFormFields = () => {
    const fieldsToTouch: AssistedAffiliationFieldName[] = [
      "representativeNames",
      "representativeLastNames",
      "representativeEmail",
      "representativePhone",
      "identityNumber",
      "passportNumber",
      "nationality",
      "documentNumber",
      "commercialName",
      "monthlySales",
      "businessType",
      "province",
      "municipality",
      "sector",
      "neighborhood",
      "streetAndNumber",
      "reference",
      "bank",
      "accountNumber",
      "accountType",
      "officerConsultant",
      "executiveCode",
      "executiveEmail",
      "cashManagementEmail",
      "commercialRepresentative",
      "creationStatus",
      "internalComments",
      "tapRate",
      "tapQuantity",
      "connectivityType",
      "tipEnabled",
    ];

    setTouchedFields((current) => ({
      ...current,
      ...fieldsToTouch.reduce<TouchedFields>(
        (accumulator, field) => ({ ...accumulator, [field]: true }),
        {}
      ),
    }));

    setTouchedFiles({
      frontIdentification: true,
      backIdentification: true,
      passportPhoto: true,
      accountEvidence: true,
    });
  };

  // La identificación que hay en el campo en este momento. Una validación tarda: si el usuario cambia
  // el número mientras tanto, la respuesta de la anterior no debe darse por buena para el nuevo.
  const currentDocumentNumberRef = useRef(form.documentNumber);

  const updateField = (field: AssistedAffiliationFieldName) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = field === "representativeEmail"
      ? event.target.value.toLowerCase()
      : event.target.value;

    if (field === "documentNumber") {
      currentDocumentNumberRef.current = value;
      setRncValidationExecuted(false);
      setRncValidationFeedback(null);
      setIsLegalNameManuallyEditable(false);
      setForm((current) => ({
        ...current,
        documentNumber: value,
        legalName: "",
      }));
      return;
    }

    if (field === "province") {
      setForm((current) => ({
        ...current,
        province: value,
        municipality: "",
        sector: "",
        neighborhood: "",
      }));
      return;
    }

    if (field === "municipality") {
      setForm((current) => ({
        ...current,
        municipality: value,
        sector: "",
        neighborhood: "",
      }));
      return;
    }

    if (field === "sector") {
      setForm((current) => ({
        ...current,
        sector: value,
        neighborhood: "",
      }));
      return;
    }

    if (field === "creationStatus") {
      if (value === form.creationStatus) return;
      setPendingCreationStatus(value);
      return;
    }

    setForm((current) => ({ ...current, [field]: value }));
  };

  const closeStatusChangeModal = () => {
    setPendingCreationStatus("");
  };

  const confirmStatusChange = (reason: string) => {
    const nextStatus = pendingCreationStatus;
    if (!nextStatus) return;

    setForm((current) => ({ ...current, creationStatus: nextStatus }));
    setStatusObservations((current) => [
      ...current,
      toStatusObservation({
        statusLabel: nextStatus,
        reason,
        author: user?.username || OBSERVATION_FALLBACK_AUTHOR,
      }),
    ]);
    setTouchedFields((current) => ({ ...current, creationStatus: true }));
    closeStatusChangeModal();
  };

  const markFieldAsTouched = (field: AssistedAffiliationFieldName) => () => {
    setTouchedFields((current) => ({ ...current, [field]: true }));
  };

  const updateSwitch = (field: AssistedAffiliationFieldName) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setForm((current) => ({ ...current, [field]: event.target.checked }));
  };

  const updateFile = (field: SingleAssistedAffiliationFileName) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setFiles((current) => ({ ...current, [field]: selectedFile }));
    setTouchedFiles((current) => ({ ...current, [field]: true }));
  };

  const updateLegalDocumentFiles = (field: LegalDocumentFileName) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    const fileError = selectedFiles
      .map((file) => getLegalDocumentFileError(file))
      .find(Boolean);

    if (fileError) {
      setLegalDocumentFileErrors((current) => ({ ...current, [field]: fileError }));
      event.target.value = "";
      return;
    }

    setLegalDocumentFiles((current) => ({
      ...current,
      [field]: [...current[field], ...selectedFiles],
    }));
    setLegalDocumentFileErrors((current) => ({ ...current, [field]: "" }));
    event.target.value = "";
  };

  const removeFile = (field: SingleAssistedAffiliationFileName) => {
    setFiles((current) => ({ ...current, [field]: null }));
    setTouchedFiles((current) => ({ ...current, [field]: true }));
  };

  const removeLegalDocumentFile = (
    field: LegalDocumentFileName,
    fileIndex: number
  ) => {
    setLegalDocumentFiles((current) => ({
      ...current,
      [field]: current[field].filter((_, index) => index !== fileIndex),
    }));
  };

  const shouldShowFieldError = (field: AssistedAffiliationFieldName) =>
    Boolean(hasSubmittedForm || touchedFields[field]);

  const shouldShowFileError = (field: SingleAssistedAffiliationFileName) =>
    Boolean(hasSubmittedForm || touchedFiles[field]);

  const getFieldError = (field: AssistedAffiliationFieldName) => {
    if (field === "representativeEmail" && !form.representativeEmail.trim() && !hasSubmittedForm) {
      return "";
    }

    return shouldShowFieldError(field)
      ? representativeFieldErrors[field] ||
        commerceFieldErrors[field] ||
        addressFieldErrors[field] ||
        accountFieldErrors[field] ||
        executiveFieldErrors[field] ||
        billetSmartFieldErrors[field] ||
        (missingRequiredFields.includes(field) ? "Este campo es requerido." : "")
      : "";
  };

  const getFileError = (field: SingleAssistedAffiliationFileName) =>
    shouldShowFileError(field)
      ? representativeFileErrors[field] || accountFileErrors[field]
      : "";

  const showPendingIntegrationAlert = (action: string) => {
    setAlert({
      show: true,
      type: "warning",
      text: `${action} requiere integración con el servicio de afiliación asistida.`,
    });
  };

  const renderFieldLabel = (label: string, required = false, htmlFor?: string) => (
    <InputLabel className={classes.fieldLabel} shrink htmlFor={htmlFor}>
      {label}
      {required ? <span className={classes.requiredMark}> *</span> : null}
    </InputLabel>
  );

  const renderLabeledTextField = (
    field: AssistedAffiliationFieldName,
    label: string,
    options?: {
      required?: boolean;
      disabled?: boolean;
      multiline?: boolean;
      placeholder?: string;
      type?: string;
      inputProps?: Record<string, unknown>;
      InputProps?: React.ComponentProps<typeof TextField>["InputProps"];
      onBlur?: () => void;
    }
  ) => {
    const error = getFieldError(field);
    const inputId = `assisted-affiliation-${field}`;

    return (
      <Box>
        {renderFieldLabel(label, options?.required, inputId)}
        <TextField
          id={inputId}
          className={error ? classes.invalidField : undefined}
          fullWidth
          size="small"
          variant="outlined"
          value={form[field]}
          onChange={updateField(field)}
          onBlur={() => {
            markFieldAsTouched(field)();
            options?.onBlur?.();
          }}
          required={options?.required}
          disabled={options?.disabled}
          multiline={options?.multiline}
          minRows={options?.multiline ? 3 : undefined}
          placeholder={options?.placeholder}
          type={options?.type}
          inputProps={options?.inputProps}
          InputProps={options?.InputProps}
          error={Boolean(error)}
          helperText={error}
        />
      </Box>
    );
  };

  const renderLabeledSelect = (
    field: AssistedAffiliationFieldName,
    label: string,
    options: SelectOption[],
    required = false,
    disabled = false
  ) => {
    const error = getFieldError(field);
    const inputId = `assisted-affiliation-${field}`;

    return (
      <Box>
        {renderFieldLabel(label, required, inputId)}
        <TextField
          id={inputId}
          className={error ? classes.invalidField : undefined}
          select
          fullWidth
          size="small"
          variant="outlined"
          value={form[field]}
          onChange={updateField(field)}
          onBlur={markFieldAsTouched(field)}
          required={required}
          disabled={disabled}
          error={Boolean(error)}
          helperText={error}
          SelectProps={{
            inputProps: { "aria-label": label },
          }}
        >
          <MenuItem value="">
            <em>Seleccione una opción</em>
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  };

  const renderFileControl = (
    field: SingleAssistedAffiliationFileName,
    title: string,
    required = false,
    accept = ".jpg,.jpeg,.pdf"
  ) => (
    <FileUploadField
      field={field}
      title={title}
      file={files[field]}
      error={getFileError(field)}
      required={required}
      accept={accept}
      onChange={updateFile}
      onRemove={removeFile}
    />
  );

  const renderLegalDocuments = () => (
    <LegalDocumentsUpload
      documents={legalDocuments}
      files={legalDocumentFiles}
      errors={visibleLegalDocumentFileErrors}
      onFilesChange={updateLegalDocumentFiles}
      onRemoveFile={removeLegalDocumentFile}
      onAddDocument={() => showPendingIntegrationAlert("Adjuntar documento")}
    />
  );

  const handleValidateIdentification = async () => {
    const documentNumber = form.documentNumber.trim();
    const isValidDocumentNumber =
      NUMERIC_PATTERN.test(documentNumber) &&
      documentNumber.length >= 9 &&
      documentNumber.length <= 11;

    if (!isValidDocumentNumber) return;

    setIsValidatingIdentification(true);
    const validation = ++identificationValidationRef.current;
    const isStillInField = () =>
      currentDocumentNumberRef.current.trim() === documentNumber;

    try {
      const commerceDgiiInfo = await getCommerceDgiiInfoService(documentNumber);
      const legalName = commerceDgiiInfo?.name?.trim() || "";
      const commercialName =
        commerceDgiiInfo?.business_name?.trim() || legalName;
      const feedback = addCommercialNameToBilletFeedback(
        await validateIdentificationService(documentNumber),
        commercialName
      );

      if (!isStillInField()) return;

      setRncValidationFeedback(feedback);
      setRncValidationExecuted(true);
      setIsLegalNameManuallyEditable(feedback.tone === "success" && !legalName);
      setForm((current) => ({
        ...current,
        legalName: legalName || current.legalName,
        commercialName: current.commercialName || commercialName,
      }));

      if (feedback.tone === "success" && !legalName) {
        setAlert({
          show: true,
          type: "warning",
          text: "La identificación fue validada, pero no se pudo autocompletar la razón social desde DGII.",
        });
      }
    } catch (error) {
      // El error de un número que ya no está en el campo no le sirve al usuario.
      if (!isStillInField()) return;

      setRncValidationExecuted(false);
      setRncValidationFeedback(null);
      setAlert({
        show: true,
        type: "error",
        text: getValidateIdentificationErrorMessage(error),
      });
    } finally {
      // Una respuesta tardía de un número anterior no apaga el indicador de la validación en curso.
      if (validation === identificationValidationRef.current) setIsValidatingIdentification(false);
    }
  };

  const renderRncValidationAdornment = () => {
    if (isValidatingIdentification) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={18} />
        </InputAdornment>
      );
    }

    if (!rncValidationExecuted || commerceFieldErrors.documentNumber) return null;

    if (rncValidationFeedback?.tone === "warning") {
      return (
        <InputAdornment position="end">
          <WarningIcon className={classes.rncWarningAdornment} />
        </InputAdornment>
      );
    }

    if (rncValidationFeedback?.tone === "error") {
      return (
        <InputAdornment position="end">
          <ErrorIcon className={classes.rncErrorAdornment} />
        </InputAdornment>
      );
    }

    return (
      <InputAdornment position="end">
        <CheckCircleIcon className={classes.rncAdornment} />
      </InputAdornment>
    );
  };

  const scrollToFirstInvalidSection = () => {
    const invalidSectionId =
      hasRepresentativeErrors
        ? "representative-section"
        : hasCommerceErrors
        ? "commerce-section"
        : Object.keys(addressFieldErrors).length > 0
        ? "address-section"
        : Object.keys(accountFieldErrors).length > 0 ||
          Object.keys(accountFileErrors).length > 0
        ? "account-section"
        : missingRequiredLegalDocuments.length > 0 ||
          Object.values(legalDocumentFileErrors).some(Boolean)
        ? "legal-documents-section"
        : hasExecutiveErrors
        ? "executive-section"
        : missingRequiredFields.includes("commercialRepresentative") ||
          missingRequiredFields.includes("creationStatus")
        ? "commercial-management-section"
        : !form.billetSmart
        ? "affiliation-section"
        : hasBilletSmartErrors
        ? "billet-smart-section"
        : null;

    if (!invalidSectionId) return;

    window.requestAnimationFrame(() => {
      document
        .getElementById(invalidSectionId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // El formulario guarda identificadores y el servicio de creación pide además los nombres: se
  // resuelven contra el mismo catálogo que pinta cada select.
  const labelOf = (options: SelectOption[], value: string) =>
    options.find((option) => option.value === value)?.label ?? "";

  const catalogLabels = {
    provinceName: labelOf(catalogs.provinceOptions, form.province),
    municipalityName: labelOf(catalogs.municipalityOptions, form.municipality),
    sectorName: labelOf(catalogs.sectorOptions, form.sector),
    neighborhoodName: labelOf(catalogs.neighborhoodOptions, form.neighborhood),
    detailCategory: labelOf(catalogs.businessTypeOptions, form.businessType),
  };

  const handleEmailBlur = () => {
    const email = form.representativeEmail.trim();
    if (!email || (edit && email === edit.defaultForm.representativeEmail.trim())) return;

    markFieldAsTouched("representativeEmail")();
    void emailValidation.validate();
  };

  const closeEmailValidationDialog = () => setIsEmailValidationDialogOpen(false);

  const validateEmailBeforeSave = async () => {
    markFieldAsTouched("representativeEmail")();
    const valid = await emailValidation.validate();
    if (valid === null) return false;
    if (valid) return true;

    setIsEmailValidationDialogOpen(true);
    document.getElementById("representative-section")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    return false;
  };

  const validateEmailForDraft = async () => {
    const email = form.representativeEmail.trim();
    if (!email) return false;
    if (edit && email === edit.defaultForm.representativeEmail.trim()) return true;
    markFieldAsTouched("representativeEmail")();
    return emailValidation.validate();
  };

  /**
   * Detiene el guardado si algún campo que el operador cambió respecto de `baseline` tiene un error:
   * lo marca, avisa y lleva a su sección. Devuelve si lo detuvo.
   */
  const blockOnChangedFieldErrors = (baseline: AssistedAffiliationForm): boolean => {
    // Un campo con error no viaja al servicio, pero solo se mira lo que el operador cambió: la
    // solicitud llega con datos que la pantalla marca en rojo sin que él los haya escrito, y
    // bloquearlo por eso lo dejaría sin poder guardar, o lo empujaría a reescribir un dato ajeno
    // para poder continuar. Se compara contra el formulario de partida (la solicitud tal como se
    // cargó, o el formulario vacío al crear) y no contra los campos tocados, porque un campo que
    // recibió el foco no siempre cambió. Van en el orden de la pantalla.
    const changedFieldErrors = (
      [
        ["representative-section", representativeFieldErrors],
        ["commerce-section", commerceFieldErrors],
        ["address-section", addressFieldErrors],
        ["account-section", accountFieldErrors],
        ["executive-section", executiveFieldErrors],
        // Con Billet Smart apagado la sección oculta sus campos: un error ahí no se podría corregir.
        ["billet-smart-section", form.billetSmart ? billetSmartFieldErrors : {}],
      ] as const
    ).map(([sectionId, errors]) => ({
      sectionId,
      fields: (Object.keys(errors) as AssistedAffiliationFieldName[]).filter(
        // El correo se valida aparte y, si falla, se omite sin bloquear el resto del borrador.
        (field) => field !== "representativeEmail" && form[field] !== baseline[field]
      ),
    }));

    // Solo cuenta un archivo que el operador adjuntó con formato o tamaño inválido, porque la solicitud
    // no trae archivos. La falta del comprobante no detiene el guardado: es obligatorio para crear el
    // comercio, y `accountFileErrors` lo marca por eso aunque nadie haya adjuntado nada.
    const hasInvalidAttachedFile = Boolean(getRepresentativeFileError(files.accountEvidence, false));

    const firstInvalidSection = changedFieldErrors.find(
      ({ sectionId, fields }) =>
        fields.length > 0 || (hasInvalidAttachedFile && sectionId === "account-section")
    );

    if (firstInvalidSection) {
      // Se marca solo lo que el operador cambió. Marcar el formulario entero pintaría en rojo los
      // obligatorios de "Crear comercio", como los documentos, que en un borrador nadie ha tocado.
      setTouchedFields((current) =>
        changedFieldErrors
          .flatMap(({ fields }) => fields)
          .reduce<TouchedFields>((accumulator, field) => ({ ...accumulator, [field]: true }), current)
      );
      if (hasInvalidAttachedFile) {
        setTouchedFiles((current) => ({ ...current, accountEvidence: true }));
      }
      setAlert({
        show: true,
        type: "warning",
        text: DRAFT_FIELDS_INVALID_MESSAGE,
      });
      window.requestAnimationFrame(() => {
        document
          .getElementById(firstInvalidSection.sectionId)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return true;
    }

    return false;
  };

  const saveDraft = async () => {
    if (!edit) {
      const includeEmail = await validateEmailForDraft();
      if (includeEmail === null) return;
      await handleCreateDraft(includeEmail);
      return;
    }

    // Sin nombre comercial no se manda nada. Se marca solo ese campo: marcar el formulario entero
    // pintaría en rojo los obligatorios de "Crear comercio", que un borrador no exige.
    if (!form.commercialName.trim()) {
      setTouchedFields((current) => ({ ...current, commercialName: true }));
      setAlert({
        show: true,
        type: "warning",
        text: COMMERCIAL_NAME_REQUIRED_MESSAGE,
      });
      document
        .getElementById("commerce-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const includeEmail = await validateEmailForDraft();
    if (includeEmail === null) return;

    if (blockOnChangedFieldErrors(edit.defaultForm)) return;

    try {
      const observations = statusObservations;
      await edit.onSave(form, {
        observations, labels: catalogLabels, clearRepresentativeEmail: !includeEmail,
      });
      // Solo se descartan las que viajaron: un cambio de estado confirmado mientras se guardaba
      // queda para el siguiente guardado. Si el guardado falla, todas se conservan para reintentar.
      setStatusObservations((current) => current.slice(observations.length));
      setAlert({
        show: true,
        type: "success",
        text: DRAFT_SAVE_SUCCESS_MESSAGE,
      });
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        text: getDraftErrorMessage(error, DRAFT_SAVE_ERROR_MESSAGE),
      });
    }
  };

  const handleSaveDraft = async () => {
    if (draftSaveInFlightRef.current || edit?.isSaving) return;

    // La referencia bloquea un segundo clic antes de que React deshabilite el botón.
    draftSaveInFlightRef.current = true;
    setIsSavingDraft(true);

    try {
      await saveDraft();
    } finally {
      draftSaveInFlightRef.current = false;
      setIsSavingDraft(false);
    }
  };

  const handleSaveDraftWithoutEmail = () => {
    closeEmailValidationDialog();
    void handleSaveDraft();
  };

  const handleCreateCommerce = async () => {
    const guard = commerceCreationGuardRef.current;
    if (hasCreatedCommerce || guard.inFlight || guard.completed) return;

    setHasSubmittedForm(true);
    touchFormFields();

    if (hasMissingRequiredData) {
      setIsMissingRequiredDataOpen(true);
      return;
    }

    if (!(await validateEmailBeforeSave())) return;

    if (!isRncValidationSuccessful) {
      setAlert({
        show: true,
        type: hasBlockingRncValidation ? "error" : "warning",
        text: hasBlockingRncValidation
          ? "La identificación tiene restricciones que impiden crear el comercio."
          : "Ejecuta la validación de la identificación antes de crear el comercio.",
      });
      document
        .getElementById("commerce-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (hasFormValidationErrors) {
      setAlert({
        show: true,
        type: "warning",
        text: "Revisa los campos con validaciones antes de crear el comercio.",
      });
      scrollToFirstInvalidSection();
      return;
    }

    setIsCreateConfirmationOpen(true);
  };

  const handleConfirmCreateCommerce = async () => {
    if (
      hasCreatedCommerce ||
      isCreatingCommerce ||
      commerceCreationGuardRef.current.inFlight ||
      commerceCreationGuardRef.current.completed
    ) {
      return;
    }

    if (hasMissingRequiredData) {
      setIsCreateConfirmationOpen(false);
      setIsMissingRequiredDataOpen(true);
      return;
    }

    if (!(await validateEmailBeforeSave())) return;

    setIsCreatingCommerce(true);

    try {
      const created = await createCommerceOnce(
        commerceCreationGuardRef.current,
        () => createDraftService(formToDraftPayload(form, catalogLabels))
      );
      if (!created) return;
      // Una respuesta resuelta representa una creación aceptada por el servicio. El bloqueo se
      // conserva hasta salir del formulario para impedir repetir la misma solicitud.
      setHasCreatedCommerce(true);
      setIsCreateConfirmationOpen(false);
      if (onCreated) {
        onCreated();
      } else {
        setAlert({
          show: true,
          type: "success",
          text: "Solicitud creada exitosamente.",
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        text: getDraftErrorMessage(error),
      });
    } finally {
      setIsCreatingCommerce(false);
    }
  };

  const closeMissingRequiredDataModal = () => setIsMissingRequiredDataOpen(false);

  const renderAffiliationError = () =>
    hasSubmittedForm && !form.billetSmart ? (
      <Typography variant="caption" color="error">
        Billet Smart es requerido para crear el comercio.
      </Typography>
    ) : null;

  /**
   * Primer guardado de una solicitud nueva: la crea en el servicio. Exige la identificación validada
   * sin incidencias, la razón social, el nombre comercial y que ningún campo escrito tenga error; lo
   * demás se completa después. Si el correo no pasó la validación, viaja vacío en el envío.
   * Comparte el candado con "Crear comercio" porque los dos crean el mismo registro: un segundo POST
   * solo encontraría la solicitud ya creada.
   */
  const handleCreateDraft = async (includeEmail: boolean) => {
    const guard = commerceCreationGuardRef.current;
    if (guard.inFlight || guard.completed) return;

    if (!isRncValidationSuccessful) {
      setAlert({
        show: true,
        type: hasBlockingRncValidation ? "error" : "warning",
        text: hasBlockingRncValidation
          ? DRAFT_CREATE_VALIDATION_BLOCKED_MESSAGE
          : DRAFT_CREATE_VALIDATION_REQUIRED_MESSAGE,
      });
      document
        .getElementById("commerce-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (!form.legalName.trim() || !form.commercialName.trim()) {
      // Solo se marcan estos dos: un borrador no exige el resto, y pintarlo en rojo diría lo contrario.
      setTouchedFields((current) => ({
        ...current,
        legalName: true,
        commercialName: true,
      }));
      setAlert({
        show: true,
        type: "warning",
        text: DRAFT_CREATE_NAMES_REQUIRED_MESSAGE,
      });
      document
        .getElementById("commerce-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (blockOnChangedFieldErrors(initialAssistedAffiliationForm)) return;

    try {
      const created = await createCommerceOnce(guard, () =>
        createDraftService(withValidatedDraftEmail(formToDraftPayload(form, catalogLabels), includeEmail))
      );
      if (!created) return;

      const notice: AlertState = {
        show: true,
        type: "success",
        text: "Solicitud creada exitosamente.",
      };
      if (onCreated) {
        onCreated();
      } else {
        setAlert(notice);
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        text: getDraftErrorMessage(error, DRAFT_CREATE_ERROR_MESSAGE),
      });
    }
  };

  const summaryRows = buildRequestSummaryRows(form, {
    ...edit?.summary,
    updatedAt: edit?.lastUpdatedAt,
  });

  const sections = [
    {
      content: (
        <FormSection id="representative-section" number={1} title="Datos del representante">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderLabeledTextField("representativeNames", "Nombre(s)", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledTextField("representativeLastNames", "Apellido(s)", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              <RepresentativeEmailField
                value={form.representativeEmail}
                onChange={updateField("representativeEmail")}
                onBlur={handleEmailBlur}
                validation={emailValidation.state}
                error={getFieldError("representativeEmail")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {renderLabeledTextField("representativePhone", "Celular", {
                required: true,
                inputProps: { inputMode: "numeric", maxLength: 12 },
              })}
            </Grid>
            <Grid item xs={12} md={2}>
              {renderFieldLabel("¿Es extranjero?", true)}
              <RadioGroup
                row
                value={form.isForeignRepresentative ? "Sí" : "No"}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    isForeignRepresentative: event.target.value === "Sí",
                  }))
                }
              >
                {yesNoOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={(
                      <Radio
                        color="default"
                        icon={<RadioButtonUncheckedIcon className={classes.stepRadio} />}
                        checkedIcon={<RadioButtonCheckedIcon className={classes.stepRadioChecked} />}
                      />
                    )}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
              {!form.isForeignRepresentative && form.identityNumber ? (
                <Typography variant="caption" color="textSecondary">
                  Br: {form.identityNumber}
                </Typography>
              ) : null}
            </Grid>
            {!form.isForeignRepresentative ? (
              <>
                <Grid item xs={12} md={6}>
                  {renderLabeledTextField("identityNumber", "Número de cédula", {
                    required: true,
                    inputProps: { inputMode: "numeric", maxLength: 11 },
                  })}
                </Grid>
                <Grid item xs={12} md={6} />
                <Grid item xs={12} md={6}>
                  {renderFileControl(
                    "frontIdentification",
                    "Foto de la cédula frontal",
                    true,
                    ".jpg,.jpeg,.pdf"
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderFileControl(
                    "backIdentification",
                    "Foto de la cédula dorsal",
                    true,
                    ".jpg,.jpeg,.pdf"
                  )}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Box className={classes.passportCard}>
                  <Box className={classes.passportHeader}>
                    <Typography variant="subtitle2">Datos del pasaporte</Typography>
                    <ExpandLessIcon fontSize="small" />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {renderLabeledSelect(
                        "nationality",
                        "Código de país",
                        nationalityOptions,
                        true
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {renderLabeledTextField("passportNumber", "Número de pasaporte", {
                        required: true,
                        inputProps: { maxLength: 9 },
                      })}
                    </Grid>
                    <Grid item xs={12}>
                      {renderFileControl(
                        "passportPhoto",
                        "Foto o documento del pasaporte",
                        true,
                        ".jpg,.jpeg,.pdf"
                      )}
                      <Typography variant="caption" color="textSecondary">
                        Formatos permitidos: JPG, PDF. Máx. 25MB
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="commerce-section" number={2} title="Datos del comercio">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderLabeledTextField("documentNumber", "Número de identificación (RNC/Cédula)", {
                required: true,
                // Identifica la solicitud ante el servicio: una vez creada, ya no se cambia.
                disabled: isEditing,
                inputProps: { inputMode: "numeric", minLength: 9, maxLength: 11 },
                InputProps: {
                  endAdornment: renderRncValidationAdornment(),
                },
                onBlur: isEditing ? undefined : handleValidateIdentification,
              })}
            </Grid>
            {rncValidationExecuted &&
            !commerceFieldErrors.documentNumber &&
            rncValidationFeedback ? (
              <Grid item xs={12}>
                <RncValidationFeedbackCard feedback={rncValidationFeedback} />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {renderFieldLabel("Nombre legal (Razón social)", true, "assisted-affiliation-legalName")}
              <TextField
                id="assisted-affiliation-legalName"
                fullWidth
                size="small"
                variant="outlined"
                value={form.legalName}
                onChange={updateField("legalName")}
                onBlur={markFieldAsTouched("legalName")}
                required
                disabled={!isLegalNameManuallyEditable}
                placeholder="Autocompletar con DGII"
                className={`${isLegalNameManuallyEditable ? "" : classes.lockedInput} ${getFieldError("legalName") ? classes.invalidField : ""}`}
                inputProps={{
                  "aria-describedby": getFieldError("legalName") ? "legal-name-error" : undefined,
                }}
                InputProps={
                  isLegalNameManuallyEditable
                    ? undefined
                    : {
                        endAdornment: (
                          <InputAdornment position="end">
                            <LockOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }
                }
                error={Boolean(getFieldError("legalName"))}
              />
              <Typography className={classes.fieldHelper}>
                {isLegalNameManuallyEditable
                  ? "DGII no devolvió la razón social. Complétala manualmente."
                  : "Este dato proviene de DGII y no puede ser modificado."}
              </Typography>
              {getFieldError("legalName") ? (
                <Typography id="legal-name-error" variant="caption" color="error">
                  {getFieldError("legalName")}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("commercialName", "Nombre comercial", {
                inputProps: { maxLength: 100 },
                required: true,
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledSelect(
                "monthlySales",
                "Aproximado de ventas mensuales",
                monthlySalesOptions,
                true
              )}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledSelect(
                "businessType",
                "Tipo de negocio",
                catalogs.businessTypeOptions,
                true,
                catalogLoading.initial
              )}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="address-section" number={3} title="Datos de la dirección">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "province",
                "Provincia",
                catalogs.provinceOptions,
                true,
                catalogLoading.initial
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "municipality",
                "Municipio",
                catalogs.municipalityOptions,
                true,
                !form.province || catalogLoading.municipalities
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "sector",
                "Sector",
                catalogs.sectorOptions,
                true,
                !form.municipality || catalogLoading.sectors
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "neighborhood",
                "Paraje / Barrio",
                catalogs.neighborhoodOptions,
                true,
                !form.sector || catalogLoading.neighborhoods
              )}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("streetAndNumber", "Calle y número", {
                required: true,
                inputProps: { maxLength: 100 },
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("reference", "Referencia", {
                inputProps: { maxLength: 100 },
              })}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="account-section" number={4} title="Cuenta para recibir los fondos">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderLabeledSelect(
                "bank",
                "Banco",
                catalogs.bankOptions,
                true,
                catalogLoading.initial
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderLabeledTextField("accountNumber", "Número de cuenta", {
                required: true,
                inputProps: { inputMode: "numeric" },
              })}
            </Grid>
            <Grid item xs={12}>
              {renderFieldLabel("Tipo de cuenta", true)}
              <RadioGroup
                row
                aria-label="Tipo de cuenta"
                aria-invalid={Boolean(getFieldError("accountType"))}
                aria-describedby={getFieldError("accountType") ? "account-type-error" : undefined}
                className={getFieldError("accountType") ? classes.requiredControlError : undefined}
                value={form.accountType}
                onChange={updateField("accountType")}
              >
                {accountTypeOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={(
                      <Radio
                        color="default"
                        icon={<RadioButtonUncheckedIcon className={classes.stepRadio} />}
                        checkedIcon={<RadioButtonCheckedIcon className={classes.stepRadioChecked} />}
                      />
                    )}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
              {getFieldError("accountType") ? (
                <Typography id="account-type-error" variant="caption" color="error">
                  {getFieldError("accountType")}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              {renderFileControl("accountEvidence", "Evidencia de la cuenta bancaria", true)}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection
          id="legal-documents-section"
          number={5}
          title="Documentos legales"
          subtitle="Adjunte todos los documentos requeridos para la creación del comercio."
        >
          {renderLegalDocuments()}
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="executive-section" number={6} title="Datos del ejecutivo">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderLabeledTextField("officerConsultant", "Oficial/Consultor", {
                inputProps: { maxLength: 100 },
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("executiveCode", "Código de colaborador Ejecutivo BHD", {
                inputProps: { maxLength: 50 },
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("executiveEmail", "Correo electrónico Ejecutivo BHD", {
                type: "email",
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField(
                "cashManagementEmail",
                "Correo electrónico Ejecutivo Cash Management",
                { type: "email" }
              )}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="commercial-management-section" number={7} title="Gestión comercial">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderLabeledTextField("commercialRepresentative", "Representante comercial", {
                required: true,
              })}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledSelect(
                "creationStatus",
                "Estado de creación del comercio",
                buildCreationStatusOptions(form.creationStatus),
                true,
                !isEditing
              )}
            </Grid>
            <Grid item xs={12}>
              {renderLabeledTextField("internalComments", "Comentarios internos", {
                multiline: true,
                placeholder: "Escribe un comentario o nota interna sobre esta solicitud...",
                inputProps: { maxLength: INTERNAL_COMMENTS_MAX_LENGTH },
              })}
            </Grid>
          </Grid>
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection
          id="affiliation-section"
          number={8}
          title="Tipo de afiliación"
          required
          subtitle="Activa los medios de cobro que deseas habilitar para este comercio."
        >
          <AffiliationToggleList form={form} onToggle={updateSwitch} />
          {renderAffiliationError()}
        </FormSection>
      ),
    },
    {
      content: (
        <FormSection id="billet-smart-section" number={9} title="Configuración de Billet Smart">
          {form.billetSmart ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {renderLabeledTextField("tapRate", "Tasa (%)", {
                  type: "number",
                  inputProps: { step: "0.01" },
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderLabeledTextField("tapQuantity", "Cantidad", {
                  type: "number",
                  inputProps: { min: 1, step: 1 },
                })}
              </Grid>
              <Grid item xs={12}>
                {renderFieldLabel("Tipo de conectividad")}
                <RadioGroup
                  row
                  className={classes.billetSmartRadioGroup}
                  value={form.connectivityType}
                  onChange={updateField("connectivityType")}
                >
                  {connectivityOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={(
                        <Radio
                          color="default"
                          icon={<RadioButtonUncheckedIcon className={classes.billetSmartRadio} />}
                          checkedIcon={<RadioButtonCheckedIcon className={classes.billetSmartRadioChecked} />}
                        />
                      )}
                      label={option.label}
                      classes={{ label: classes.billetSmartRadioLabel }}
                    />
                  ))}
                </RadioGroup>
                {getFieldError("connectivityType") ? (
                  <Typography variant="caption" color="error">
                    {getFieldError("connectivityType")}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                {renderFieldLabel("Propina")}
                <RadioGroup
                  row
                  className={classes.billetSmartRadioGroup}
                  value={form.tipEnabled}
                  onChange={updateField("tipEnabled")}
                >
                  {yesNoOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={(
                        <Radio
                          color="default"
                          icon={<RadioButtonUncheckedIcon className={classes.billetSmartRadio} />}
                          checkedIcon={<RadioButtonCheckedIcon className={classes.billetSmartRadioChecked} />}
                        />
                      )}
                      label={option.label}
                      classes={{ label: classes.billetSmartRadioLabel }}
                    />
                  ))}
                </RadioGroup>
                {getFieldError("tipEnabled") ? (
                  <Typography variant="caption" color="error">
                    {getFieldError("tipEnabled")}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Activa Billet Smart en Tipo de afiliación para configurar este producto.
            </Typography>
          )}
        </FormSection>
      ),
    },
    {
      content: <RequestSummary id="summary-section" rows={summaryRows} />,
    },
    // El historial existe solo para una solicitud ya guardada, así que no aparece al crear una nueva.
    ...(edit
      ? [{ content: <RequestHistory id="history-section" observations={edit.observations} /> }]
      : []),
  ];

  return (
    <>
      <Box className={classes.manualRequest}>
        <RequestActionToolbar
          status={form.creationStatus}
          lastUpdatedAt={edit?.lastUpdatedAt}
          createDisabled={
            isValidatingIdentification ||
            emailValidation.state.status === "validating" ||
            isSavingDraft ||
            Boolean(edit?.isSaving) ||
            isCreatingCommerce ||
            hasCreatedCommerce
          }
          isCreating={isCreatingCommerce}
          saveLabel={isEditing ? "Guardar cambios" : undefined}
          isSaving={isSavingDraft || edit?.isSaving}
          onBack={onBack}
          onSaveDraft={handleSaveDraft}
          onCreateCommerce={handleCreateCommerce}
        />

        <Box className={classes.sectionsStack}>
          {sections.map((section, index) => (
            <Box key={index}>{section.content}</Box>
          ))}
        </Box>
      </Box>

      <Toaster
        show={alert.show}
        type={alert.type}
        text={alert.text}
        onClose={() => setAlert((current) => ({ ...current, show: false }))}
      />
      <CustomDialog
        variant="confirmation"
        icon={<HelpOutlineIcon />}
        isOpen={isCreateConfirmationOpen}
        title={CREATE_COMMERCE_CONFIRMATION_MODAL.title}
        text={CREATE_COMMERCE_CONFIRMATION_MODAL.description}
        acceptButtonLabel={CREATE_COMMERCE_CONFIRMATION_MODAL.confirmLabel}
        cancelButtonLabel={CREATE_COMMERCE_CONFIRMATION_MODAL.cancelLabel}
        actionsDisabled={isCreatingCommerce}
        okFunction={handleConfirmCreateCommerce}
        notOkFunction={() => {}}
        openCloseFx={setIsCreateConfirmationOpen}
      />
      <StatusChangeConfirmationModal
        isOpen={Boolean(pendingCreationStatus)}
        nextStatusLabel={pendingCreationStatus}
        onCancel={closeStatusChangeModal}
        onConfirm={confirmStatusChange}
      />
      <MissingRequiredDataModal
        isOpen={isMissingRequiredDataOpen}
        onClose={closeMissingRequiredDataModal}
        onExited={scrollToFirstInvalidSection}
      />
      <CustomDialog
        isOpen={isEmailValidationDialogOpen}
        title="Revisa el correo electrónico"
        text={EMAIL_SAVE_ERROR}
        acceptButtonLabel="Entendido"
        showCancelButton={false}
        okFunction={handleSaveDraftWithoutEmail}
        notOkFunction={closeEmailValidationDialog}
        openCloseFx={setIsEmailValidationDialogOpen}
      />
    </>
  );
};

export default AssistedAffiliationManualRequest;
